// @ts-strict-ignore
import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';

import { appInject } from 'src/app/app-injector';
import { FootnotesService } from 'src/app/core/services/footnotes/footnotes.service';
import { IsWithinModalService } from 'src/app/core/services/is-within-modal/is-within-modal.service';
import { RouterEventsService } from 'src/app/core/services/router-events/router-events.service';
import { hasChanged } from 'src/app/utils/functions/change-helpers';

export const IN_MODAL_IDENTIFIER = 'inModal';

const GENERIC_FOOTNOTE_PREFIX = 'owcc';

@Component({
  selector: 'cc-footnote-hint',
  templateUrl: './footnote-hint.component.html',
  styleUrl: './footnote-hint.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})

/**
 * The generic footnote hint component requires the whole footnote content, a reference which is in the following format: <team>_<component>_<number>,
 * and (if the footnote is inside a modal) an optional module-id
 */
export class FootnoteHintComponent implements OnChanges, OnInit {
  @Input() public hint!: string | number;
  @Input() public context: string = this.footnotesService.globalContext;
  @Input() public section: string = this.footnotesService.globalSection;
  @Input() public component = '';

  public content!: string;
  public reference!: string;
  public moduleId!: string;

  public constructor(
    private readonly footnotesService: FootnotesService,
    private readonly routerEventsService: RouterEventsService,
    private readonly isWithinModalService: IsWithinModalService,
    private readonly elementRef: ElementRef
  ) {}

  public ngOnChanges({ hint }: SimpleChanges): void {
    if (hasChanged(hint)) {
      if (typeof hint.previousValue === 'string' && hint.previousValue.length > 0) {
        this.footnotesService.removeFootnote(hint.previousValue, this.context, this.section);
      }
      if (typeof hint.currentValue === 'string' && hint.currentValue.length > 0) {
        this.footnotesService.addFootnote(hint.currentValue, this.context, this.section);
      }
    }
  }

  public ngOnInit(): void {
    // as footnote collections are cleared on route changes, surviving footnote
    // hints have to re-register themselves into the collection afterwards.
    this.routerEventsService.navigationEnd$
      .pipe(
        takeUntilDestroyed(appInject(DestroyRef)),
        filter(() => !!this.hint)
      )
      .subscribe(() => {
        this.footnotesService.addFootnote(`${this.hint}`, this.context, this.section);
      });

    this.content = this.footnotesService.getFootnote(`${this.hint}`)?.text;

    /*
    The generic footnote hint component requires a reference in the following format: <team>_<component>_<number>.
    As value for team, we will always use 'owcc'.
    If there are multiple footnotes in the same component (e.g. summary), the reference must not be the same, otherwise only one hint
    will be displayed. Therefore the footnote service counts every occurence per footnote and gives us the current count as number.
    */
    this.reference = `${GENERIC_FOOTNOTE_PREFIX}_${this.component}_${
      this.footnotesService.getFootnote(`${this.hint}`)?.counter
    }`;

    // If the footnote is displayed within a modal, the module id is required so the generic footnote component is rendered with a white background
    this.moduleId = this.isWithinModalService.checkElement(this.elementRef) ? IN_MODAL_IDENTIFIER : null;
  }
}
